import { useState } from 'react';
import { Button, Input, PasswordInput, Separator } from '@tutero/ui';

import {
  confirmAuthenticator,
  disableSecondFactor,
  enrolAuthenticator,
  enrolTextFactor,
  phoneNumberOf,
  removePhoneNumber,
  sendPhoneCode,
  verifyPhoneNumber,
  type Account,
  type AuthenticatorEnrolment,
} from '@/lib/account';

import { Field, SettingsSection } from './SettingsSection';
import { reasonFor, useAccountWrite } from './useAccountWrite';

/**
 * Whether enrolling an authenticator is offered. Off by the operator's call, 2026-11-02: the
 * Flutter admin has the texted code and nothing else, and the authenticator was ours to add,
 * so it waits out of sight until it is wanted. Flip this to `true` to offer it again —
 * `AuthenticatorPanel` and its `src/lib/account.ts` calls are kept and still covered.
 */
const AUTHENTICATOR_IS_OFFERED = false;

/**
 * 2FA (`settings-is-a-dialog` D7, D8, D15): a code texted to a proved number,
 * and — when `AUTHENTICATOR_IS_OFFERED` — an authenticator app; both the auth Worker serves,
 * both with one off switch.
 *
 * Off is what the Flutter popup cannot do at all: nothing in `admin-app` calls `unenroll`,
 * so its "Add 2 Factor Authentication" section only ever adds, and an account that enrols
 * twice holds two factors with no way back. An account already holding an authenticator
 * still gets `TurnOffPanel`, so hiding the enrolment strands nobody.
 *
 * Every enable and every disable takes the current password, because the Worker's routes
 * do. No password and no `otpauth://` address is logged, put in an address or cached: all
 * of it is state in the panel that owns it, dropped with the panel.
 */
export function SecondFactorSection({ account }: { account: Account }) {
  const aboveTheText = account.twoFactorEnabled ? (
    <TurnOffPanel />
  ) : AUTHENTICATOR_IS_OFFERED ? (
    <AuthenticatorPanel />
  ) : null;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-bold-m">2FA</h3>
        <p className="text-muted-foreground text-regular-s">
          {account.twoFactorEnabled
            ? '2FA is on. Signing in asks for a code as well as your password.'
            : '2FA is off. Your password alone signs you in.'}
        </p>
      </div>
      {/* The rule has nothing to separate when the text panel is the only panel. */}
      {aboveTheText === null ? null : (
        <>
          {aboveTheText}
          <Separator />
        </>
      )}
      <TextPanel account={account} />
    </div>
  );
}

/** The off switch for both methods: one route, one password (D7). */
function TurnOffPanel() {
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const turnOff = useAccountWrite(disableSecondFactor);

  return (
    <SettingsSection
      title="Turn 2FA off"
      about="Your password alone will sign you in again."
      error={turnOff.isError ? reasonFor(turnOff.error) : null}
      done={done ? '2FA is off.' : null}
      onSubmit={() => {
        if (password === '' || turnOff.isPending) return;
        setDone(false);
        turnOff.mutate(password, {
          onSuccess: () => {
            setDone(true);
            setPassword('');
          },
        });
      }}
      footer={
        <Button
          type="submit"
          variant="destructive"
          disabled={password === '' || turnOff.isPending}
        >
          {turnOff.isPending ? 'Turning Off…' : 'Turn Off'}
        </Button>
      }
    >
      <Field id="settings-2fa-off-password" label="Your current password">
        <PasswordInput
          id="settings-2fa-off-password"
          value={password}
          autoComplete="current-password"
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
    </SettingsSection>
  );
}

/**
 * The authenticator: the password buys the secret, and the app's first code proves it
 * arrived. No QR — a renderer is a dependency this repo does not carry, and every app
 * takes the address by hand (D8).
 *
 * Exported for its suite alone while `AUTHENTICATOR_IS_OFFERED` is false: the section does
 * not draw it, and the behaviour stays covered rather than written twice later.
 */
export function AuthenticatorPanel() {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [enrolment, setEnrolment] = useState<AuthenticatorEnrolment | null>(
    null,
  );
  const begin = useAccountWrite(enrolAuthenticator);
  const finish = useAccountWrite(confirmAuthenticator);

  if (enrolment === null) {
    return (
      <SettingsSection
        title="Authenticator app"
        about="A code from an app on your phone, which works with no signal."
        error={begin.isError ? reasonFor(begin.error) : null}
        onSubmit={() => {
          if (password === '' || begin.isPending) return;
          begin.mutate(password, {
            onSuccess: (data) => {
              setEnrolment(data);
              setPassword('');
            },
          });
        }}
        footer={
          <Button type="submit" disabled={password === '' || begin.isPending}>
            {begin.isPending ? 'Setting Up…' : 'Set Up Authenticator'}
          </Button>
        }
      >
        <Field id="settings-totp-password" label="Your current password">
          <PasswordInput
            id="settings-totp-password"
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection
      title="Authenticator app"
      summary="Add this address to your authenticator, then give it the first code it shows."
      error={finish.isError ? reasonFor(finish.error) : null}
      onSubmit={() => {
        if (code.trim() === '' || finish.isPending) return;
        finish.mutate(code.trim(), {
          onSuccess: () => {
            /* The secret leaves the screen the moment it is no longer needed (D17). */
            setEnrolment(null);
            setCode('');
          },
        });
      }}
      footer={
        <Button type="submit" disabled={code.trim() === '' || finish.isPending}>
          {finish.isPending ? 'Turning On…' : 'Turn On'}
        </Button>
      }
    >
      <Field id="settings-totp-uri" label="Setup address">
        <code
          id="settings-totp-uri"
          className="bg-muted text-regular-s block overflow-x-auto rounded-md p-2 font-mono break-all"
        >
          {enrolment.totpURI}
        </code>
      </Field>
      <div className="flex flex-col gap-1">
        <p className="text-bold-s">Backup codes</p>
        <p className="text-muted-foreground text-regular-s">
          Keep these somewhere safe. They are shown once and each works once, if
          you lose the app.
        </p>
        <ul className="text-regular-s grid grid-cols-2 gap-1 font-mono">
          {enrolment.backupCodes.map((backup) => (
            <li key={backup}>{backup}</li>
          ))}
        </ul>
      </div>
      <Field id="settings-totp-code" label="Code from the app">
        <Input
          id="settings-totp-code"
          value={code}
          inputMode="numeric"
          autoComplete="one-time-code"
          onChange={(event) => setCode(event.target.value)}
        />
      </Field>
    </SettingsSection>
  );
}

/**
 * The texted code, which is two acts (D15): prove the number, then make it the second
 * step. The Worker refuses `method: 'otp'` for an account with no proved number, so the
 * order is not ours to choose.
 */
function TextPanel({ account }: { account: Account }) {
  return account.phoneNumber === null ? (
    <ProveNumberPanel />
  ) : (
    <ProvedNumberPanel account={account} />
  );
}

/** Act one: a number, a texted code, and the number is the account's (D14). */
function ProveNumberPanel() {
  const [typed, setTyped] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const send = useAccountWrite(sendPhoneCode);
  const prove = useAccountWrite(
    ({ number, code: given }: { number: string; code: string }) =>
      verifyPhoneNumber(number, given),
  );

  /* One field holding the whole number: the country-code picker beside a digits-only field
     is what put two plus signs on every Flutter enrolment (D13.9). */
  const number = phoneNumberOf(typed);
  const malformed = typed.trim() !== '' && number === null;

  if (sentTo !== null) {
    return (
      <SettingsSection
        title="Texted code"
        summary={`We texted a code to ${sentTo}.`}
        error={prove.isError ? reasonFor(prove.error) : null}
        onSubmit={() => {
          if (code.trim() === '' || prove.isPending) return;
          prove.mutate({ number: sentTo, code: code.trim() });
        }}
        footer={
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => {
                setSentTo(null);
                setCode('');
              }}
            >
              Use Another Number
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={code.trim() === '' || prove.isPending}
            >
              {prove.isPending ? 'Checking…' : 'Verify This Number'}
            </Button>
          </div>
        }
      >
        <Field id="settings-text-code" label="Code from the text">
          <Input
            id="settings-text-code"
            value={code}
            inputMode="numeric"
            autoComplete="one-time-code"
            onChange={(event) => setCode(event.target.value)}
          />
        </Field>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection
      title="Texted code"
      about="A code texted to your phone. The number is proved before it can be used."
      error={send.isError ? reasonFor(send.error) : null}
      onSubmit={() => {
        if (number === null || send.isPending) return;
        send.mutate(number, { onSuccess: () => setSentTo(number) });
      }}
      footer={
        <Button type="submit" disabled={number === null || send.isPending}>
          {send.isPending ? 'Texting…' : 'Text Me a Code'}
        </Button>
      }
    >
      <Field
        id="settings-phone"
        label="Phone number"
        about="With its country prefix, as +61 412 345 678."
      >
        <Input
          id="settings-phone"
          type="tel"
          value={typed}
          autoComplete="tel"
          onChange={(event) => setTyped(event.target.value)}
        />
      </Field>
      {malformed ? (
        <p role="alert" className="text-destructive text-regular-s">
          Enter the number with its country prefix, as +61 412 345 678.
        </p>
      ) : null}
    </SettingsSection>
  );
}

/** Act two, once the number is proved: make it the second step, or take it off (D15). */
function ProvedNumberPanel({ account }: { account: Account }) {
  const [password, setPassword] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const use = useAccountWrite(enrolTextFactor);
  const remove = useAccountWrite(removePhoneNumber);
  const failure = use.isError
    ? reasonFor(use.error)
    : remove.isError
      ? reasonFor(remove.error)
      : null;

  return (
    <SettingsSection
      title="Texted code"
      summary={`${account.phoneNumber} is proved and ready.`}
      error={failure}
      done={done}
      onSubmit={() => {
        if (account.twoFactorEnabled || password === '' || use.isPending)
          return;
        setDone(null);
        use.mutate(password, {
          onSuccess: () => {
            setDone('A texted code is now your second step.');
            setPassword('');
          },
        });
      }}
      footer={
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={remove.isPending}
            onClick={() => {
              setDone(null);
              remove.mutate(undefined, {
                onSuccess: () => setDone('That number is off your account.'),
              });
            }}
          >
            {remove.isPending ? 'Removing…' : 'Remove This Number'}
          </Button>
          {account.twoFactorEnabled ? null : (
            <Button
              type="submit"
              className="flex-1"
              disabled={password === '' || use.isPending}
            >
              {use.isPending ? 'Turning On…' : 'Use Texted Codes'}
            </Button>
          )}
        </div>
      }
    >
      {account.twoFactorEnabled ? (
        <p className="text-muted-foreground text-regular-s">
          Signing in texts a code to this number.
        </p>
      ) : (
        <Field id="settings-text-password" label="Your current password">
          <PasswordInput
            id="settings-text-password"
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
      )}
    </SettingsSection>
  );
}
